/**
 * FramrContext - React context provider for Framr
 * 
 * CHANGE HISTORY:
 * - 251025_1651 [pending] danielbeer: Initial creation
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { FramrManager } from './FramrManager.js';
import type { FrameConfig, FrameContext } from './types.js';

const FramrReactContext = createContext<FrameContext | null>(null);

export interface FramrProviderProps {
  manager?: FramrManager;
  initialConfig?: FrameConfig;
  children: React.ReactNode;
}

export function FramrProvider({ manager: externalManager, initialConfig, children }: FramrProviderProps) {
  const [manager] = useState(() => externalManager || new FramrManager(initialConfig));
  const [config, setConfig] = useState<FrameConfig>(manager.getConfig());

  useEffect(() => {
    // Subscribe to configuration changes
    const unsubscribe = manager.subscribe((newConfig) => {
      setConfig(newConfig);
    });

    return unsubscribe;
  }, [manager]);

  const updateConfig = useCallback((updates: Partial<FrameConfig>) => {
    manager.updateConfig(updates);
  }, [manager]);

  const contextValue: FrameContext = {
    config,
    updateConfig,
    isWhiteLabeled: manager.isWhiteLabeled(),
    orgId: manager.getOrgId(),
    appName: manager.getAppName(),
  };

  return (
    <FramrReactContext.Provider value={contextValue}>
      {children}
    </FramrReactContext.Provider>
  );
}

/**
 * Hook to access Framr context
 */
export function useFramr(): FrameContext {
  const context = useContext(FramrReactContext);
  
  if (!context) {
    throw new Error('useFramr must be used within a FramrProvider');
  }
  
  return context;
}

/**
 * Hook to check if feature is enabled
 */
export function useFeature(feature: string): boolean {
  const { config } = useFramr();
  return config.features?.[feature] ?? false;
}

/**
 * Hook to get context value
 */
export function useFrameContext<T = any>(key: string): T | undefined {
  const { config } = useFramr();
  return config.context?.[key] as T | undefined;
}

/**
 * Hook to check permission
 */
export function usePermission(permission: string): boolean {
  const { config } = useFramr();
  return config.permissions?.includes(permission) ?? false;
}
